import type { ReactElement } from 'react';
import {
  AVATAR_ANIMAL_KEYS,
  AVATAR_SCENE_KEYS,
  normalizeAvatarKey,
  avatarAnimalSrc,
  avatarSceneSrc,
} from '../../../../../shared/ember-avatars.js';
import { PresenceAvatar } from './PresenceAvatar';
import './AvatarPicker.css';

/**
 * The one avatar chooser (§0.9): a live preview of the current picture, then
 * two ingredient grids — the animal, then the background — each tap saving a
 * full combo immediately. Mounted by the Profile header's avatar sheet (its
 * one home since /settings/space retired, 2026-09-01); a second grid would be
 * a second source of truth for how a person becomes their picture.
 *
 * The grids show each half by itself (the animal on a plain paper disc, the
 * scene painting alone): the animal image is full-bleed over the scene, so a
 * combo swatch shows the animal a hundred times and hides the background
 * being chosen. The composed picture lives in the sticky preview instead,
 * which answers every tap while either grid is scrolled.
 */
export function AvatarPicker({ avatarKey, busy = false, onPick }: {
  /** Current avatar_id; anything stale normalizes onto the same combo the
   *  rendered avatar shows, so the "current" marks always agree with it. */
  avatarKey: string;
  busy?: boolean;
  /** Receives the full `animal:scene` combo of the tapped swatch. */
  onPick: (comboKey: string) => void;
}): ReactElement {
  const combo = normalizeAvatarKey(avatarKey);
  return (
    <div className="avatar-picker">
      <div className="avatar-picker-preview">
        <PresenceAvatar avatarKey={`${combo.animal}:${combo.scene}`} size={76} />
        <p className="avatar-picker-preview-name">
          {combo.animal.replaceAll('-', ' ')} · {combo.scene.replaceAll('-', ' ')}
        </p>
      </div>
      <div className="avatar-picker-groups">
        <div>
          <p className="avatar-picker-label">Animal</p>
          <div className="avatar-picker-grid" role="listbox" aria-label="Choose your animal">
            {AVATAR_ANIMAL_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                role="option"
                aria-label={key.replaceAll('-', ' ')}
                aria-selected={key === combo.animal}
                className={`avatar-picker-swatch${key === combo.animal ? ' is-current' : ''}`}
                disabled={busy}
                data-log="presence_avatar_save"
                onClick={() => onPick(`${key}:${combo.scene}`)}
              >
                <span className="avatar-picker-disc avatar-picker-disc-animal" aria-hidden>
                  <img
                    src={avatarAnimalSrc(key, 80)}
                    srcSet={`${avatarAnimalSrc(key, 40)} 1x, ${avatarAnimalSrc(key, 80)} 2x`}
                    alt=""
                    width={40}
                    height={40}
                    loading="lazy"
                    draggable={false}
                  />
                </span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="avatar-picker-label">Background</p>
          <div className="avatar-picker-grid" role="listbox" aria-label="Choose your background">
            {AVATAR_SCENE_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                role="option"
                aria-label={key.replaceAll('-', ' ')}
                aria-selected={key === combo.scene}
                className={`avatar-picker-swatch${key === combo.scene ? ' is-current' : ''}`}
                disabled={busy}
                data-log="presence_avatar_save"
                onClick={() => onPick(`${combo.animal}:${key}`)}
              >
                <span className="avatar-picker-disc" aria-hidden>
                  <img
                    src={avatarSceneSrc(key, 80)}
                    srcSet={`${avatarSceneSrc(key, 40)} 1x, ${avatarSceneSrc(key, 80)} 2x`}
                    alt=""
                    width={40}
                    height={40}
                    loading="lazy"
                    draggable={false}
                  />
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
